import { useState } from "react";
import { Helmet } from "react-helmet";
import wrapperHOC from "@Utils/wrapperHOC";
import Modal from "@Components/Modal";
import Portal from "@Components/Portal";
import GroupBookingForm from "./components/GroupBookingForm/GroupBookingForm";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { EVENT_LABEL } from "constants/index";
import V2Footer from "@Components/V2Footer/V2Footer";
// Navbar and Reveal are shared components rather than per-page copies, so the v2
// pages keep one source of truth for the scroll/hover interactions.
import Navbar from "@Components/Navbar/Navbar";
import Hero, { DEFAULT_GROUP } from "./components/Hero/Hero";
// Lived with the ticket variant (GroupBookingV2) while both cuts were up, imported
// from there so the shared section couldn't drift. That page has been removed, so
// this section now belongs to this page.
import Steps from "./components/Steps/Steps";
import styles from "./GroupBookingV2.module.scss";
import { CONTACT_URL, GROUP_BOOKINGS_URL, HOW_IT_WORKS_URL } from "constants/configConstants";
import useTranslationHook from "hooks/useTranslationHook";
import useIsApp from "hooks/useIsApp";

/**
 * Group Booking.
 *
 * Started as the avatars variant, one of two cuts built for comparison: the group
 * appears as a row of faces rather than a 3D ticket, and the live total sits inside
 * the headline. The ticket cut (GroupBookingV2, /group-booking-v2) has since been
 * removed, so this is the only Group Booking page.
 */
const GroupBookingV2 = () => {
  const { t } = useTranslationHook();
  const isApp = useIsApp();
  // Lives here, not in the hero, because two CTAs read it: the hero's own button and
  // the navbar's, which takes over once the hero scrolls away.
  const [groupSize, setGroupSize] = useState(DEFAULT_GROUP);
  const [showForm, setShowForm] = useState(false);

  /**
   * Opens the page's own two-step enquiry form — a port of the one on the page this
   * replaces, living in `components/GroupBookingForm`.
   *
   * ⚠️  DELIBERATELY NOT THE SHARED `LeadFormModal`. That was tried first and cannot
   * work here: it is always opened from a property or a search region, so a location
   * travels with it inside `inventory`, and this page has neither — the API answers
   * `400 {"message":"Error location-not-present"}` and the lead is lost. Making the
   * shared modal fit meant changing routing every other page relies on.
   */
  const handleClaimClick = (size: number) => {
    gtagEventEmitter({
      action: EVENT_LABEL.GROUP_BOOKINGS_CLICKED,
      category: "group_booking",
      label: `group_of_${size}`,
    });
    setShowForm(true);
  };

  return (
    <div className={styles.page}>
      <Helmet title={t("groupbooking.v2.meta.title")}>
        <meta name="description" content={t("groupbooking.v2.meta.description")} />
        {/* Instrument Sans — the design font, loaded only on this page */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&display=swap"
        />
      </Helmet>

      {/* ⚠️  NO "Visit amber" HERE, and no secondary treatment either.

          This bar used to fall back to the Navbar's DEFAULT CTA — "Visit amber",
          pointing at the homepage — rendered as a soft grey chip so it would not
          compete with the hero. That was answering the wrong question: this page's one
          action is claiming the group discount, and a button offering to leave for the
          homepage sat exactly where that action belongs.

          It carries the page's real CTA instead, revealed only once the hero has
          scrolled away, so the two never share the screen and there is no competition
          to design around. Same arrangement /list uses with `data-lwu-hero`. */}
      <Navbar
        isAppHeader={isApp}
        addAppPadding={false}
        links={[
          { label: "navbar.v2.howitworks", href: HOW_IT_WORKS_URL },
          { label: "navbar.v2.support", href: CONTACT_URL },
        ]}
        ctaLabel="groupbooking.v2.cta.claim"
        ctaHref={GROUP_BOOKINGS_URL}
        ctaOnClick={() => handleClaimClick(groupSize)}
        ctaRevealAfter="[data-gb-hero]"
      />
      <Hero
        onClaimClick={handleClaimClick}
        groupSize={groupSize}
        onGroupSizeChange={setGroupSize}
      />
      <Steps />
      {/* Remaining sections to come. */}
      <V2Footer />

      {/* Mounted only while open, so none of it is requested until someone asks. */}
      {showForm && (
        <Portal rootId="forPortal">
          {/* `size=""` applies no size class, and no `align` either, so the panel's whole
              geometry comes from `.formPanel` in this page's stylesheet — centred card on
              desktop, bottom sheet below 650px.
              It used to be size="loginModalWidth" align="center". `loginModalWidth` is a
              flat `width: 440px` with no max-width and no media query, so on a 390px phone
              the card was wider than the screen: fields clipped on the right and the close
              button pushed off-screen (PLG-3348). That class is shared with
              `components/Login`, so it is fixed here rather than there. */}
          <Modal hideModal={() => setShowForm(false)} size="" className={styles.formPanel}>
            <GroupBookingForm groupSize={groupSize} onClose={() => setShowForm(false)} />
          </Modal>
        </Portal>
      )}
    </div>
  );
};

export default wrapperHOC(GroupBookingV2, {
  componentName: "GroupBookingV2-Page",
  showForChina: true,
});
